/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateX,
  useStateXAtomSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import {
  Box,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  BooleanExpressionProp,
  BooleanExpressionValue,
} from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import { expressionPropsAtom } from 'expression/builder/state';
import Icon from 'icons/Icon';
import React, { memo, useEffect, useId, useState } from 'react';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';

import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    marginTop: 8,
    alignItems: 'center',
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  label: {
    flex: 1,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  inner: {
    display: 'flex',
    width: 120,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    margin: '0 4px',
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
  tip: {
    marginLeft: 8,
  },
  margin: {
    marginBottom: 16,
  },
}));

const useOptionStyles = makeStyles((theme: Theme) => ({
  option: {
    display: 'flex',
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    height: '2rem',
    outline: 'none',
    position: 'relative',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s',
    '& > svg': {
      fontSize: '1rem',
      alignSelf: 'center',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.4,
  },
}));

function Option({
  children,
  selected,
  onSelect,
  tip,
  isViewOnlyUser = false,
}: {
  children: React.ReactChild;
  selected?: boolean;
  onSelect: () => void;
  tip: string;
  isViewOnlyUser: boolean;
}) {
  const classes = useOptionStyles();

  return (
    <div
      data-tip={tip}
      onKeyDown={(e) => clickOnEnterOrSpace(e, onSelect)}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      className={clsx(
        classes.option,
        selected ? classes.selected : null,
        isViewOnlyUser ? classes.disabled : null,
      )}
      aria-label={tip}
    >
      {children}
    </div>
  );
}

function OnOffExpressionProperty({
  activeItemId,
  def,
  mgr,
  appUid,
  pageId,
  property,
  onChange,
  value: text2 = getDefaultValues<BooleanExpressionValue>(mgr, property),
  isViewOnlyUser = false,
}: PropertyEditorProps<BooleanExpressionProp, BooleanExpressionValue>) {
  const text: BooleanExpressionValue =
    typeof text2 === 'boolean' ? { value: text2, type: 'boolean' } : text2;
  const classes = useStyles();
  const [value, setValue] = useState(
    text.type === 'expression' ? text.value : '',
  );
  useEffect(() => {
    if (text.type === 'expression') {
      setValue(text.value);
    }
  }, [text.type, text.value]);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  const setExpressionProps = useStateXAtomSetter(expressionPropsAtom, {
    params: { appUid, pageId, itemId: activeItemId },
  });

  // do not use the designer path, instead use some transient path
  // as this could be used in UpdateProp Action in which case we need to
  // call onChange to change the property and not update the designer path directly
  const path = ['transient', '_expression_', activeItemId, property];
  const [expressionValue, setExpressionValue] = useStateX<
    BooleanExpressionValue | undefined
  >(path, undefined);

  useEffect(() => {
    // reset the value in the trasient path
    setExpressionValue(undefined);
  }, [setExpressionValue]);

  useEffect(() => {
    if (
      expressionValue !== undefined &&
      expressionValue.type === 'expression'
    ) {
      // user have selected some item in the popup
      setValue(expressionValue.value);
      onChange(property, expressionValue);
    }
  }, [expressionValue, onChange, property, setValue]);
  const id = useId();
  const openExpressionBuilder = () => {
    if (text.type === 'expression') {
      setExpressionProps({
        path,
        returnType: 'boolean',
        value: { type: 'expression', value: text.value },
        context: {
          appUid,
          pageId,
          itemId: activeItemId,
          property,
          data: {},
        },
        type: 'expressionObject',
        open: true,
      });
    }
  };

  let index = 2;
  if (text.type === 'boolean') {
    index = text.value ? 0 : 1;
  }

  const inner = (
    <div
      className={clsx(
        classes.inner,
        text.type === 'boolean' ? null : classes.margin,
      )}
    >
      <Box
        sx={{
          width: `${100 / 3 - 4}%`,
          left: `${(100 / 3) * index}%`,
        }}
        className={classes.s}
      />
      <Option
        isViewOnlyUser={isViewOnlyUser}
        onSelect={() => {
          onChange(property, { value: true, type: 'boolean' });
        }}
        selected={text.type === 'boolean' && text.value}
        tip="On"
      >
        On
      </Option>
      <Option
        isViewOnlyUser={isViewOnlyUser}
        onSelect={() => {
          onChange(property, { value: false, type: 'boolean' });
        }}
        selected={text.type === 'boolean' && !text.value}
        tip="Off"
      >
        Off
      </Option>
      <Option
        isViewOnlyUser={isViewOnlyUser}
        onSelect={() => {
          if (text.type === 'expression') {
            openExpressionBuilder();
          } else {
            setValue('');
            onChange(property, { value: '', type: 'expression' });
          }
        }}
        selected={text.type === 'expression'}
        tip={
          text.type === 'expression' ? 'Open Expression Builder' : 'Expression'
        }
      >
        <Icon disabled={isViewOnlyUser} iconName="sigma" />
      </Option>
    </div>
  );

  return (
    <div data-testid={property} className={classes.root}>
      {text.type === 'boolean' ? (
        <>
          <div className={classes.label}>{def.label}</div>
          {inner}
        </>
      ) : (
        <FormControl
          disabled={isViewOnlyUser}
          variant="standard"
          className={classes.label}
          classes={{ root: classes.input }}
          fullWidth
        >
          <InputLabel htmlFor={id}>{def.label}</InputLabel>
          <Input
            disabled={isViewOnlyUser}
            id={id}
            spellCheck={false}
            autoCorrect="off"
            fullWidth
            onFocus={(e) => {
              e.target.select();
            }}
            onBlur={() => {
              if (value !== text.value) {
                onChange(property, { type: 'expression', value });
              }
            }}
            type="text"
            value={value ?? ''}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            endAdornment={
              <InputAdornment position="end">{inner}</InputAdornment>
            }
          />
        </FormControl>
      )}
      {helpText ? (
        <Icon
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </div>
  );
}

export default memo(OnOffExpressionProperty);
